import { FormEvent, useRef, useState } from 'react'
import { object, setLocale, string } from 'yup'
import styles from '../assets/scss/components/newsletter-form.module.scss'
import { FormError } from '../lib/types'
import { useI18n } from './i18n.client'
import { useTracking } from '../lib/hooks/tracking.client'

interface NewsletterFormErrorState {
  email?: string
}

export const NewsletterForm: React.FC<{ variant: string }> = ({ variant = 'newsletter_subscription_footer' }) => {
  const [i18n] = useI18n()
  const form = useRef(null)
  const defaultErrors = {
    email: undefined,
  }
  const [errors, setErrors] = useState<NewsletterFormErrorState>(defaultErrors)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const { trackEvent } = useTracking()

  setLocale({
    mixed: {
      required: ({ path }) => ({ id: 'yup.mixed.required', replacements: { path: i18n.translate(path) } }),
    },
    string: {
      email: ({ path }) => ({ id: 'yup.string.email', replacements: { path: i18n.translate(path) } }),
    },
  })

  const formSchema = object().shape({
    email: string().email().required(),
  })

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (form.current) {
      const formData = new FormData(form.current)
      const formValues = Object.fromEntries(formData)
      const isFormValid = await formSchema.isValid(formValues, { abortEarly: false })

      setErrors(defaultErrors)

      if (isFormValid) {
        setIsDisabled(true)

        const postUrl = new URL(window.location.origin + '/api/klaviyo/create-profile')
        const response = await fetch(postUrl, {
          body: JSON.stringify(formValues),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: '2025-01-15',
          },
          method: 'POST',
        })

        const json = await response.json()
        if (json.data) {
          const postUrl = new URL(window.location.origin + '/api/klaviyo/add-profile-to-list')
          await fetch(postUrl, {
            body: JSON.stringify({ id: json.data.id }),
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              revision: '2025-01-15',
            },
            method: 'POST',
          })

          setIsComplete(true)
          trackEvent(variant)
        } else if (json.errors) {
          setErrors({ email: json.errors[0].detail })
          setIsDisabled(false)
        }
      } else {
        formSchema.validate(formValues, { abortEarly: false }).catch((validationErrors) => {
          const reducedErrors = validationErrors.inner.reduce((accumulatedErrors: FormError, error: FormError) => {
            if (error.path) {
              return {
                ...accumulatedErrors,
                [error.path]: i18n.translate(error.message.id, error.message.replacements),
              }
            }
          }, {})

          setErrors(reducedErrors)
        })
      }
    }
  }

  if (isComplete) {
    return <div className={styles.success}>{i18n.translate('thanks-for-subscribing')}</div>
  }

  return (
    <>
      <form className={styles.form} noValidate onSubmit={handleSubmit} ref={form}>
        <input
          autoComplete="email"
          className={styles.textInput}
          name="email"
          placeholder={`${i18n.translate('email')}*`}
          type="email"
        />
        <button className={styles.button} disabled={isDisabled} type="submit">
          {i18n.translate('send')}
        </button>
      </form>
      {errors.email && <div className={styles.error}>{errors.email}</div>}
    </>
  )
}
